import { LIMITS } from '../data/limits';
import {
  ABOUT_WEAK_HOOKS,
  BUZZWORDS,
  CTA_SIGNALS,
  FIRST_PERSON_SIGNALS,
  THIRD_PERSON_SIGNALS,
} from '../data/vocabulary';
import {
  charCount,
  findPhrases,
  gradient,
  hasMetric,
  hasParagraphBreak,
  leadingWindow,
  normalise,
  trailingWindow,
} from '../text';
import type { Rule, RuleContext } from '../types';
import { defineRule, findingsFor, flag, pass, skip } from './helpers';

/**
 * About-section rules.
 *
 * Two facts drive almost everything here. First, LinkedIn truncates About after
 * roughly `seeMoreTarget` characters, so the opening lines do all the work and
 * the rest is only read by people you already convinced. Second, About is the
 * one place on the profile where you write in your own voice — which is why the
 * voice rules below are not stylistic nitpicks.
 */

const f = findingsFor('about');

/** The About text with whitespace normalised. Paragraph rules use the raw text. */
const find = (ctx: RuleContext) => normalise(ctx.profile.about);

export const aboutRules: readonly Rule[] = [
  defineRule({
    id: 'about.missing',
    section: 'about',
    weight: 3,
    evaluate(ctx) {
      if (find(ctx)) return pass();
      return flag(
        0,
        f(
          'missing',
          'critical',
          'Your About section is empty',
          'About is the only place you get to explain your work in your own words, and LinkedIn indexes every word of it for search. An empty one leaves a visible blank on your profile and forfeits roughly 2,600 characters of free keyword space.',
          'Write four short paragraphs: what you do now, proof you can do it, what you are looking for, and how to reach you. Aim for 400–1,200 characters.'
        )
      );
    },
  }),

  defineRule({
    id: 'about.length',
    section: 'about',
    weight: 2,
    evaluate(ctx) {
      const about = find(ctx);
      if (!about) return skip(); // `about.missing` already covers this.

      const length = charCount(about);

      if (length > LIMITS.about.hard) {
        return flag(
          0.2,
          f(
            'too-long',
            'critical',
            `Your About section is ${length} characters — over LinkedIn's ${LIMITS.about.hard} limit`,
            `LinkedIn will not save past ${LIMITS.about.hard} characters, so the ending you wrote — usually the part with your contact details — is being cut off.`,
            `Trim to under ${LIMITS.about.hard} characters. The closing call to action matters more than the middle, so cut from the middle.`
          )
        );
      }

      if (length < LIMITS.about.minRecommended) {
        return flag(
          gradient(length, 40, LIMITS.about.minRecommended),
          f(
            'too-short',
            'important',
            `Your About section is only ${length} characters`,
            `At this length it reads as an afterthought, and you are leaving most of the ${LIMITS.about.hard} available characters — every one of them searchable — unused.`,
            `Get to at least ${LIMITS.about.minRecommended} characters by adding one concrete piece of evidence: a project, a number, or a problem you solved.`
          )
        );
      }

      return pass();
    },
  }),

  defineRule({
    id: 'about.hook',
    section: 'about',
    weight: 3,
    evaluate(ctx) {
      const about = find(ctx);
      if (!about) return skip();

      const hook = leadingWindow(about, LIMITS.about.seeMoreTarget);
      const weakOpeners = findPhrases(hook, ABOUT_WEAK_HOOKS);
      const buzzwords = findPhrases(hook, BUZZWORDS);
      const problems = [...weakOpeners, ...buzzwords];

      if (problems.length === 0) return pass();

      return flag(
        gradient(problems.length, 3, 0),
        f(
          'weak-hook',
          'important',
          'Your opening lines do not earn the click',
          `Only about the first ${LIMITS.about.seeMoreTarget} characters show before "…see more". Yours opens with phrasing that thousands of other profiles also use, so a reader has no reason to expand it.`,
          'Open with your strongest specific claim instead — a result, a number, or the exact problem you solve. Save the introductions for later in the paragraph.',
          problems
        )
      );
    },
  }),

  defineRule({
    id: 'about.no-metrics',
    section: 'about',
    weight: 2,
    evaluate(ctx) {
      const about = find(ctx);
      if (!about) return skip();
      if (hasMetric(about)) return pass();

      return flag(
        0.3,
        f(
          'no-metrics',
          'important',
          'Nothing in your About section is quantified',
          'There is not a single number, percentage, or amount anywhere in it. Unquantified claims read as opinion; a number turns the same sentence into evidence a reader can weigh.',
          'Add at least one figure. "Built several web apps" becomes "Built 4 web apps, one used by 300+ students on campus". Team size, users, hours saved, and grades all count.'
        )
      );
    },
  }),

  defineRule({
    id: 'about.voice',
    section: 'about',
    weight: 2,
    evaluate(ctx) {
      const about = find(ctx);
      if (!about) return skip();

      // Only the opening is checked. Later in the section people legitimately
      // describe other people ("she led the team I joined"), and flagging that
      // would be a false positive.
      const opening = leadingWindow(about, LIMITS.about.seeMoreTarget);
      const thirdPerson = findPhrases(opening, THIRD_PERSON_SIGNALS);
      const firstPerson = findPhrases(opening, FIRST_PERSON_SIGNALS);

      if (thirdPerson.length === 0) return pass();
      // Mixed voice is still worth flagging, but it is a smaller problem than a
      // section written entirely as a press release.
      const consistent = firstPerson.length === 0;

      return flag(
        consistent ? 0.3 : 0.6,
        f(
          'third-person',
          consistent ? 'important' : 'polish',
          'Your About section is written about you, not by you',
          'Third-person self-description reads as a press release written by someone else. LinkedIn is a conversation, and first person is what makes the section feel like one.',
          'Rewrite in first person: "She has 5 years of experience in analytics" becomes "I have spent 5 years in analytics".',
          thirdPerson
        )
      );
    },
  }),

  defineRule({
    id: 'about.wall-of-text',
    section: 'about',
    weight: 1,
    evaluate(ctx) {
      // Deliberately reads the raw text: `normalise` collapses the blank lines
      // this rule exists to look for.
      const raw = ctx.profile.about;
      if (!raw.trim()) return skip();

      const length = charCount(raw);
      if (length <= LIMITS.about.wallOfTextThreshold) return pass();
      if (hasParagraphBreak(raw)) return pass();

      return flag(
        0.4,
        f(
          'wall-of-text',
          'polish',
          `${length} characters with no paragraph breaks`,
          'An unbroken block of text on a phone screen is skipped rather than read. Blank lines give the eye somewhere to land and make the section skimmable.',
          'Break it into 2–4 short paragraphs of two or three sentences each, separated by a blank line. A few "•" bullets for your strongest results work well too.'
        )
      );
    },
  }),

  defineRule({
    id: 'about.no-cta',
    section: 'about',
    weight: 2,
    evaluate(ctx) {
      const about = find(ctx);
      if (!about) return skip();

      // A call to action belongs at the end, so only the closing window counts.
      // An email address or a URL anywhere in that window is a CTA in itself.
      const closing = trailingWindow(about, LIMITS.about.ctaWindow);
      const signals = findPhrases(closing, CTA_SIGNALS);
      const hasContact = /\S+@\S+\.\S+|https?:\/\/|(?:www|github|linkedin)\./i.test(closing);

      if (signals.length > 0 || hasContact) return pass();

      return flag(
        0.4,
        f(
          'no-cta',
          'important',
          'Your About section does not tell the reader what to do next',
          'Someone who reads to the end is as interested as they will ever be. Without a next step, that interest expires: they close the tab and you never hear about it.',
          'End with one explicit invitation and a way to act on it — "Open to SDE internships for Summer 2026 — reach out at name@email.com" or a link to your portfolio.'
        )
      );
    },
  }),

  defineRule({
    id: 'about.keyword-coverage',
    section: 'about',
    weight: 2,
    evaluate(ctx) {
      const { keywords } = ctx;
      // No target role selected means there is nothing to measure against, so
      // this rule drops out of scoring rather than scoring zero.
      if (!keywords.roleId) return skip();
      if (!find(ctx)) return skip();
      if (keywords.coverage >= LIMITS.keywords.coverageTarget) return pass();

      const percent = Math.round(keywords.coverage * 100);

      return flag(
        gradient(keywords.coverage, 0, LIMITS.keywords.coverageTarget),
        f(
          'keyword-coverage',
          'important',
          `Your profile covers only ${percent}% of the vocabulary for ${keywords.roleLabel}`,
          'Recruiters find candidates by searching for specific terms. A profile that never uses the words for the job it wants does not appear in those searches at all, however strong it is.',
          'Work the missing terms into your About and Experience where they are genuinely true of you. Never list a skill you cannot discuss in an interview.',
          keywords.missing.slice(0, 10)
        )
      );
    },
  }),
];
